import { useRouteParams } from "@vueuse/router";
import { filter, find, findIndex, some } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref, type Component } from "vue";
import { RouteQueryAndHash, useRoute, useRouter } from "vue-router";

export type NavigationGuard = () => boolean | void | Promise<boolean | void>;

export interface StepConfig {
  id: string;
  title?: string;
  disabled?: boolean;
  component?: Component;
  beforeEnter?: NavigationGuard;
  beforeLeave?: NavigationGuard;
}

export interface StepperConfig {
  steps: StepConfig[];
  defaultStep: string;
  basePath: string;
}

export const useStepperStore = defineStore("stepper", () => {
  // These will be set during initialization
  const config = ref<StepperConfig | null>(null);

  // Track validity of current step
  const isCurrentStepValid = ref(false);

  const router = useRouter();
  const route = useRoute();

  const currentStepId = useRouteParams<string>("step", "");
  const basePath = computed(() => config.value?.basePath || "");

  const currentStep = computed(() => {
    if (!config.value) return null;

    return (
      find(config.value.steps, { id: currentStepId.value }) ||
      find(config.value.steps, { id: config.value?.defaultStep })
    );
  });

  // Get all enabled steps
  const enabledSteps = computed(() => {
    if (!config.value) return [];
    return filter(config.value.steps, ({ disabled }) => !disabled);
  });

  // Current step index among enabled steps
  const currentStepIndex = computed(() => {
    if (!currentStepId.value || !config.value) return -1;

    // At this point we know config.value is not null
    return findIndex(enabledSteps.value, { id: currentStepId.value });
  });

  const hasNextStep = computed(
    () => currentStepIndex.value < enabledSteps.value.length - 1
  );

  const hasPreviousStep = computed(() => currentStepIndex.value > 0);

  const nextStep = computed(() => {
    if (!hasNextStep.value) return null;
    return enabledSteps.value[currentStepIndex.value + 1];
  });

  const previousStep = computed(() => {
    if (!hasPreviousStep.value) return null;
    return enabledSteps.value[currentStepIndex.value - 1];
  });

  // Computed to determine if a step is accessible
  const isStepAccessible = computed(() => {
    return (stepId: string): boolean => {
      const targetStepIndex = findIndex(enabledSteps.value, { id: stepId });
      if (targetStepIndex === -1) return false;

      // Current step is always accessible
      if (stepId === currentStepId.value) return true;

      // Previous steps are always accessible
      if (targetStepIndex < currentStepIndex.value) return true;

      // Next step is only accessible if current step is valid
      if (targetStepIndex === currentStepIndex.value + 1)
        return isCurrentStepValid.value;

      // Steps further ahead are not accessible
      return false;
    };
  });

  function initialize(stepperConfig: StepperConfig) {
    config.value = stepperConfig;

    // If current step is not valid, set to default
    if (
      !currentStepId.value ||
      !some(stepperConfig.steps, { id: currentStepId.value })
    ) {
      currentStepId.value = stepperConfig.defaultStep;
    }
  }

  // Set validity of current step
  function setCurrentStepValidity(isValid: boolean) {
    isCurrentStepValid.value = isValid;
  }

  async function goto(stepId: string, query: RouteQueryAndHash["query"] = {}) {
    if (!config.value) {
      console.warn("Stepper store not initialized");
      return false;
    }

    const step = config.value.steps.find((s) => s.id === stepId);

    if (!step) {
      console.warn(`Cannot navigate to step '${stepId}': step does not exist`);
      return false;
    }

    if (step.disabled) {
      console.warn(`Cannot navigate to step '${stepId}': step is disabled`);
      return false;
    }

    // Check if the target step is accessible
    if (!isStepAccessible.value(stepId)) {
      console.warn(
        `Cannot navigate to step '${stepId}': step is not accessible yet`
      );
      return false;
    }

    // Check if current step allows leaving
    if (currentStep.value?.beforeLeave) {
      const canLeave = await currentStep.value.beforeLeave();
      if (canLeave === false) return false;
    }

    // Check if destination step allows entering
    if (step.beforeEnter) {
      const canEnter = await step.beforeEnter();
      if (canEnter === false) return false;
    }

    await router.push({
      path: `${basePath.value}/${stepId}`,
      query: { ...(route.query ?? {}), ...(query ?? {}) },
    });

    return true;
  }

  async function next(query?: RouteQueryAndHash["query"]) {
    if (!hasNextStep.value || !nextStep.value) return false;

    // Only proceed if current step is valid
    if (!isCurrentStepValid.value) {
      console.warn("Cannot proceed to next step: current step is not valid");
      return false;
    }

    return goto(nextStep.value.id, query);
  }

  async function previous(query?: RouteQueryAndHash["query"]) {
    if (!hasPreviousStep.value || !previousStep.value) return false;
    return goto(previousStep.value.id, query);
  }

  return {
    // State
    currentStepId,
    isCurrentStepValid,

    // Getters
    currentStep,
    enabledSteps,
    hasNextStep,
    hasPreviousStep,
    nextStep,
    isStepAccessible,

    // Actions
    initialize,
    setCurrentStepValidity,
    goto,
    next,
    previous,
  };
});
